import { config } from '../config'
import { storage } from './storage'
import { generateVerifier, deriveChallenge, generateState } from './pkce'

interface RegisterResponse {
  client_id: string
  client_name: string
  redirect_uris: string[]
  token_endpoint_auth_method: string
}

interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  refresh_token?: string
}

// dynamic client registration (RFC 7591)
// SPA は public client なので token_endpoint_auth_method=none を必須指定
// (spec/components/worker-facade/auth-routing.md / Plan v5 レビュー v3 M3)
async function registerClient(): Promise<string> {
  const res = await fetch(`${config.facadeOrigin}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_name: config.clientName,
      redirect_uris: [config.redirectUri],
      token_endpoint_auth_method: 'none',
      grant_types: ['authorization_code'],
      response_types: ['code']
    })
  })
  if (!res.ok) throw new Error(`Client registration failed: ${res.status} ${await res.text()}`)
  const data = (await res.json()) as RegisterResponse
  storage.setClientId(data.client_id)
  return data.client_id
}

async function ensureClientId(): Promise<string> {
  return storage.getClientId() ?? (await registerClient())
}

// SPA 専用 Login (browser redirect なし、history 汚染ゼロ)
// spec/components/worker-facade/auth-routing.md / spec/components/worker-facade/auth-routing.md
export async function spaLogin(userId: string, password: string): Promise<void> {
  const clientId = await ensureClientId()
  const verifier = generateVerifier()
  const challenge = await deriveChallenge(verifier)
  const state = generateState()

  // Step 1: POST /spa-authorize で code を取得
  const authRes = await fetch(`${config.facadeOrigin}/spa-authorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      password,
      client_id: clientId,
      redirect_uri: config.redirectUri,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state
    })
  })

  if (authRes.status === 401) {
    const body = await authRes.json().catch(() => ({})) as { error_description?: string }
    throw new InvalidCredentialsError(body.error_description ?? 'IDまたはパスワードが正しくありません')
  }
  if (!authRes.ok) {
    const text = await authRes.text()
    throw new Error(`Login failed: ${authRes.status} ${text}`)
  }

  const authBody = (await authRes.json()) as { code: string; state: string }

  // Step 2: state 検証 (CSRF)
  if (authBody.state !== state) {
    throw new Error('State mismatch (CSRF protection)')
  }

  // Step 3: POST /token で code を access_token に交換 (既存 flow と同じ)
  const tokenBody = new URLSearchParams({
    grant_type: 'authorization_code',
    code: authBody.code,
    redirect_uri: config.redirectUri,
    client_id: clientId,
    code_verifier: verifier
  })

  const tokenRes = await fetch(`${config.facadeOrigin}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: tokenBody.toString()
  })
  if (!tokenRes.ok) throw new Error(`Token exchange failed: ${tokenRes.status} ${await tokenRes.text()}`)
  const tokenData = (await tokenRes.json()) as TokenResponse
  storage.setAccessToken(tokenData.access_token, tokenData.expires_in)
}

export class InvalidCredentialsError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidCredentialsError'
  }
}

export function logout(): void {
  storage.clearTokens()
  // client_id は次回起動時にも使い回せるので残す (oauth_clients への登録爆発を抑制)
}

/**
 * Permalink 用 Cookie session を発行する (POST /session)。
 * SPA login 成功直後に呼ぶ。URL は Cockpit 配下 (Pages Functions proxy) なので
 * Cookie は first-party (pages.dev) として保存される。
 * PLAN-permalink v5 / spec/components/worker-facade/auth-routing.md
 */
export async function createPermalinkSession(accessToken: string): Promise<void> {
  try {
    const res = await fetch('/session', {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!res.ok) {
      console.warn('[warn] permalink session create failed:', res.status)
    }
  } catch (e) {
    console.warn('[warn] permalink session create network error:', e)
  }
}

/**
 * Permalink Cookie session を破棄する (DELETE /session)。
 * logout 時に呼ぶ。Bearer 必須 (Cookie は Path=/v/ のため /session には届かない)。
 */
export async function deletePermalinkSession(accessToken: string): Promise<void> {
  try {
    await fetch('/session', {
      method: 'DELETE',
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  } catch (e) {
    console.warn('[warn] permalink session delete network error:', e)
  }
}
